import React from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@apollo/client';
import { black } from '@avangard/ui/colors';

import { CommonHelper } from '@helpers';
import { useEnqueueStacks } from '@modules/layout/hooks';
import { DeskCard } from '@modules/layout/moleculas';
import { DialogRemoveLapAccess } from '@modules/lap/moleculas';
import { LapAccessUpdateFormModal } from '@modules/lap/organisms';
import { RemoveLapAccessMutation } from '@modules/lap/graphql';
import { LapAccessActions } from './lap-access-actions';

import type { LapAccessEntity } from '@modules/lap/entities';
import type {
    RemoveLapAccessMutationType,
    RemoveLapAccessMutationVariables,
} from '@modules/types/graphql';

type LapAccessCardProps = {
    lapAccess: LapAccessEntity;
    onRemoveLapAccess: () => Promise<void>;
    disabled?: boolean;
    disableEdit?: boolean;
    disableRemove?: boolean;
};

const CardDetails = styled.div`
    width: 100%;

    > p {
        width: 100%;
        color: ${black[50]};
        font-size: 1.4rem;

        &:not(:last-child) {
            margin-bottom: 24px;
        }

        &:last-child {
            margin-top: -14px;
        }
    }
`;

const LapAccessCard = (props: LapAccessCardProps): React.ReactElement => {
    const { disabled, disableEdit, disableRemove, lapAccess, onRemoveLapAccess } = props;

    const { t } = useTranslation(['common', 'access']);

    const { enqueueSuccess, enqueueError } = useEnqueueStacks();

    const [removeLapAccess, { loading: removeLapAccessLoading }] = useMutation<
        RemoveLapAccessMutationType,
        RemoveLapAccessMutationVariables
    >(RemoveLapAccessMutation);

    const [openLapAccessUpdateModal, setOpenLapAccessUpdateModal] = React.useState<boolean>(false);

    const handleOpenLapAccessUpdateModal = (): void => setOpenLapAccessUpdateModal(true);
    const handleCloseLapAccessUpdateModal = (): void => setOpenLapAccessUpdateModal(false);

    const [openLapAccessRemoveModal, setOpenLapAccessRemoveModal] = React.useState<boolean>(false);

    const handleOpenLapAccessRemoveModal = (): void => setOpenLapAccessRemoveModal(true);
    const handleCloseLapAccessRemoveModal = (): void => setOpenLapAccessRemoveModal(false);

    const handleRemoveLapAccess = async (): Promise<void> => {
        try {
            const { data: removeLapAccessData } = await removeLapAccess({
                variables: { id: lapAccess.id },
            });

            const result = removeLapAccessData?.removeLapAccess;

            if (result) {
                enqueueSuccess(t('access:notifiers.remove_access.success'));

                await onRemoveLapAccess();

                handleCloseLapAccessRemoveModal();
            } else {
                enqueueError(t('access:notifiers.remove_access.error'));
            }
        } catch (e) {
            console.log(e);
            throw e;
        }
    };

    const lap = lapAccess.lap;

    const displayStudentsCount = CommonHelper.createCountFormatter(lapAccess.studentCount, [
        t('common:blocks.students.one'),
        t('common:blocks.students.some'),
        t('common:blocks.students.multiple'),
    ]);

    return (
        <>
            <DeskCard
                disabled={disabled}
                title={lap.getPlatformTitle() ?? '-'}
                ActionsComponent={
                    <LapAccessActions
                        disableEdit={disableEdit}
                        disableRemove={disableRemove}
                        onEdit={handleOpenLapAccessUpdateModal}
                        onRemove={handleOpenLapAccessRemoveModal}
                    />
                }
            >
                <CardDetails>
                    <p>{lap.course.name || lap.getProgramType() || '-'}</p>
                    <p>{lap.getFullDate()}</p>
                    <p>{lapAccess.studentCount ? displayStudentsCount : '-'}</p>
                </CardDetails>
            </DeskCard>

            <LapAccessUpdateFormModal
                lapAccess={lapAccess}
                open={openLapAccessUpdateModal}
                onClose={handleCloseLapAccessUpdateModal}
            />

            {disableRemove ? null : (
                <DialogRemoveLapAccess
                    disabled={removeLapAccessLoading}
                    open={openLapAccessRemoveModal}
                    onClose={handleCloseLapAccessRemoveModal}
                    onCancel={handleCloseLapAccessRemoveModal}
                    onAccept={handleRemoveLapAccess}
                />
            )}
        </>
    );
};

export { LapAccessCard };
